import CampaignPlatformType from '@/Enums/CampaignManagement/CampaignPlatformType.enum';
import CampaignStatus from '@/Enums/CampaignManagement/CampaignStatus.enum';
import type { ICondition } from '@/Interfaces/CampaignManagement/LineItemStatusTooltip.interface';
import IOSSwitch from '@/components/Switch';
import { getLineItemActivationConditions } from '@/helpers/campaignManagement/lineItemStatus.helpers';
import { useAppDispatch } from '@/store/hooks';
import {
  partialUpdateCampaign,
  partialUpdateDV360InsertionOrder,
  partialUpdateDV360LineItem,
  partialUpdateLineItem,
} from '@/store/slices/campaignManagementSlice';
import { Tooltip } from '@mui/material';
import { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import LineItemStatusTooltipContent from '../../components/LineItemStatusTooltip';

interface IProps {
  defaultValue?: CampaignStatus;
  row: {
    campaignId: number | undefined;
    lineItemId: number | undefined;
    type: string;
    creativesCount?: number;
    platform?: CampaignPlatformType;
  };
}

const StatusToggle = ({ defaultValue, row }: IProps) => {
  const dispatch = useAppDispatch();

  const [status, setStatus] = useState(defaultValue);
  const [isLoading, setIsLoading] = useState(false);

  const handleCheckedChange = async () => {
    const oldStatus = status;
    try {
      const newStatus = status === CampaignStatus.ACTIVE ? CampaignStatus.INACTIVE : CampaignStatus.ACTIVE;
      setStatus(newStatus);
      setIsLoading(true);
      const isDV360 = row?.platform === CampaignPlatformType.Google;
      if (row?.lineItemId !== undefined) {
        const partialUpdateAction = isDV360 ? partialUpdateDV360LineItem : partialUpdateLineItem;
        await dispatch(
          //@ts-expect-error js reducer
          partialUpdateAction({ campaignId: row?.campaignId, lineItemId: row?.lineItemId, status: newStatus }),
        ).unwrap();
      } else {
        const partialUpdateAction = isDV360 ? partialUpdateDV360InsertionOrder : partialUpdateCampaign;
        //@ts-expect-error js reducer
        await dispatch(partialUpdateAction({ campaignId: row?.campaignId, status: newStatus })).unwrap();
      }
      setIsLoading(false);
      toast.success(`${row?.lineItemId !== undefined ? 'Line Item' : 'Campaign'} updated`);
    } catch (error) {
      setStatus(oldStatus);
      toast.error(
        `Failed to update ${row?.lineItemId !== undefined ? 'Line Item' : 'Campaign'}: ${(error as Error).message}`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const checked = useMemo(() => status === CampaignStatus.ACTIVE, [status]);
  const conditions = useMemo<ICondition[]>(() => {
    if (row?.type !== 'lineItem') {
      return [];
    }
    return getLineItemActivationConditions(row);
  }, [row]);

  if (conditions?.length && conditions.some(cnd => !cnd.checked)) {
    return (
      <Tooltip title={<LineItemStatusTooltipContent conditions={conditions} />}>
        <span>
          <IOSSwitch checked={checked} disabled />
        </span>
      </Tooltip>
    );
  }

  return <IOSSwitch checked={checked} onChange={handleCheckedChange} disabled={isLoading} />;
};

export default StatusToggle;
